import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { Box, Typography, Button, TextField, CircularProgress } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import dayjs from "dayjs";

import { useClient } from "../../../context/ClientProvider";
import { useUtils } from "../../../context/UtilsProvider";
import { useLiveLoans } from "../../../context/LiveLoansProvider";


function CalculateNewRepaymentDate({ liveAccount, proposedDate, setProposedDate, setResponse }) {

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  const { createAlert, alertSeverityTypes } = useUtils();

  const { postProposePaymentDateChange } = useLiveLoans();

  const [isProposalLoading, setIsProposalLoading] = useState(false);


  const MIN_DATE_BUFFER = 8;
  const MAX_DATE_BUFFER = 30;
  const [minimumDate, setMinimumDate] = useState(dayjs().add(MIN_DATE_BUFFER, "day").startOf('day'));
  const [maximumDate, setMaximumDate] = useState(
    dayjs(liveAccount?.contractualDueDate).add(MAX_DATE_BUFFER, "day"));


  useEffect(() => {
    setProposedDate(minimumDate);
  }, []);

  async function calculate() {
    setProposedDate(proposedDate)
    const proposal = {
      "ProposedDate": proposedDate,
      "Direction": dayjs(proposedDate) < dayjs(liveAccount?.contractualDueDate) ? "Earlier" : "Later",
      "Day": proposedDate.date()
    };

    try {
      setIsProposalLoading(true);
      const response = await postProposePaymentDateChange(proposal);
      setResponse(response);
      setIsProposalLoading(false);
    } catch (error) {
      setIsProposalLoading(false);
      createAlert("There was a problem. Please try again.", alertSeverityTypes.error);
    };
  };

  return clientConfig && (
    <>
      <Box>
        <Typography sx={{ fontSize: 20, fontWeight: "bold", mb: 2 }}>
          New repayment schedule
        </Typography>
      </Box>
      <Box>
        <Box>
          <Typography
            component='label'
            htmlFor='date'
            sx={{ fontSize: 16, pt: 2, fontWeight: "bold" }}
          >
            Date
          </Typography>
        </Box>
        <Box>
          <DatePicker
            value={minimumDate}
            format="DD/MM/YYYY"
            onChange={(newValue) => setProposedDate(newValue)}
            renderInput={(params) => <TextField {...params} />}
            shouldDisableDate={(date) => date < minimumDate || date > maximumDate ||
              dayjs(date).isSame(dayjs(liveAccount?.contractualDueDate), "day")}
          />
        </Box>
      </Box>
      <Box sx={{ pt: 3 }}>
        <Button
          id="calculate-date-btn"
          onClick={() => calculate()}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: clientConfig?.layout?.buttonBorderRadius,
            width: { xs: "15em", lg: "20em" },
            backgroundColor: clientConfig?.layout?.primaryButtonBackgroundColour,
            color: clientConfig?.layout?.primaryButtonColour,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              backgroundColor: clientConfig?.layout?.hoverPrimaryButtonBackgroundColour,
              color: clientConfig?.layout?.hoverPrimaryButtonColour
            }
          }}
        >
          Calculate
        </Button>
      </Box>
      {/* Loading spinner section */}
      <Box sx={{ position: "relative", mt: 4 }}>
        {isProposalLoading && (<CircularProgress size={24} />)}
      </Box>
      {/* End of loading spinner section */}
    </>
  )
};

CalculateNewRepaymentDate.propTypes = {
  liveAccount: PropTypes.object.isRequired,
  proposedDate: PropTypes.string.isRequired,
  setProposedDate: PropTypes.func.isRequired,
  setResponse: PropTypes.func.isRequired,
};

export default CalculateNewRepaymentDate;